import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { wartendeAbnahmenQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { AufgabenVerweis } from './AufgabeKontext'
import { Button, Card, CardPad, SectionTitle } from './ui/primitives'
import { nebenBestandVerwerfen } from '../lib/mutationen'

/**
 * **Was auf meine Abnahme wartet – mit Ein-Klick-Entscheidung** ([E-88](../../../../docs/ENTSCHEIDUNGEN.md#e-88)).
 *
 * Bis zum 16.09.2026 stand diese Liste nur in „Mein Bereich" (`routes/Melden.tsx`), der
 * Ansicht des Mit-Owners. Seit [E-355](../../../../docs/ENTSCHEIDUNGEN.md#e-355) nimmt auch
 * ein **Gast** ab, was an Aufgabe oder Vorhaben auf ihn eingetragen ist – und braucht dieselbe
 * Liste in „Meine Beteiligungen". Ein Baustein für beide, damit die Abnahme an zwei Stellen
 * nicht zwei verschiedene Dinge tut. Der Server liefert jedem nur seine (`tasks.wartendeAbnahmen`).
 */
export function WartendeAbnahmen() {
  const queryClient = useQueryClient()
  const { data: abnahmen = [] } = useQuery(wartendeAbnahmenQuery)

  /**
   * **Die Notiz gehört zur Entscheidung, nicht daneben** (Nils, 17.08.2026).
   *
   * Sie wird **vor** dem Klick geschrieben und mit demselben Aufruf gespeichert. Ein Feld,
   * das erst nach dem Abnehmen erscheint, wird nicht mehr ausgefüllt – die Aufgabe ist dann
   * aus der Liste verschwunden und der Gedanke mit ihr.
   *
   * Offen ist immer nur **eine** Zeile: fünfzehn aufgeklappte Textfelder sind keine Liste
   * mehr, sondern ein Formular.
   */
  const [notizFuer, setNotizFuer] = useState<string | null>(null)
  const [notiz, setNotiz] = useState('')

  const setzen = useMutation({
    mutationFn: (v: { id: string; status: 'erledigt' | 'in_arbeit'; notiz?: string }) =>
      trpc.tasks.statusSetzen.mutate(v),
    onSuccess: async () => {
      setNotizFuer(null)
      setNotiz('')
      await nebenBestandVerwerfen(queryClient)
      await queryClient.invalidateQueries({ queryKey: ['analytics'] })
    },
  })

  /** Entscheidung absenden – mit der Notiz, falls für genau diese Zeile eine offen ist. */
  const entscheiden = (id: string, status: 'erledigt' | 'in_arbeit') =>
    setzen.mutate({
      id,
      status,
      ...(notizFuer === id && notiz.trim() ? { notiz: notiz.trim() } : {}),
    })

  if (abnahmen.length === 0) return null

  return (
    <>
      <SectionTitle hint="Steht auf Review/Abnahme und wartet auf eine Entscheidung">
        Wartet auf Abnahme
      </SectionTitle>
      <Card className="mb-4">
        <CardPad>
          {abnahmen.map((a) => (
            <div
              key={a.id}
              className="flex flex-wrap items-center gap-2 border-b border-line py-1.5 text-[12.5px] last:border-b-0"
            >
              {/**
               * **Die Wartezeit steht vorn und in Tagen.** „639 Tage" ist eine Aussage,
               * „08.11.2024" wäre eine Angabe – und die liest man ohne zu rechnen nicht.
               */}
              <span
                className={`tabular w-[76px] shrink-0 font-semibold ${
                  (a.wartetTage ?? 0) > 90
                    ? 'text-pool-sofort'
                    : (a.wartetTage ?? 0) > 14
                      ? 'text-[#8a6100]'
                      : 'text-muted'
                }`}
              >
                {a.wartetTage === null ? '– ' : `${a.wartetTage} T`}
              </span>
              <AufgabenVerweis
                aufgabeId={a.id}
                className="min-w-0 flex-1 cursor-pointer truncate text-left hover:underline"
                title={a.title}
              >
                {a.title}
              </AufgabenVerweis>
              <span className="shrink-0 text-[11px] text-muted">
                {a.bearbeiter ?? 'niemand'}
                {a.vorhabenLabel ? ` · ${a.vorhabenLabel}` : ''}
              </span>
              <Button
                size="sm"
                variant="primary"
                disabled={setzen.isPending}
                onClick={() => entscheiden(a.id, 'erledigt')}
              >
                Abnehmen
              </Button>
              {/**
               * **Der Weg zur Notiz ist ein eigener Knopf.** Ein Textfeld, das immer offen
               * steht, macht aus fünfzehn Zeilen ein Formular; eines, das es gar nicht gibt,
               * verliert die Begründung. Also: sichtbar, aber zugeklappt.
               */}
              <button
                type="button"
                onClick={() => {
                  setNotizFuer(notizFuer === a.id ? null : a.id)
                  setNotiz('')
                }}
                className="shrink-0 cursor-pointer text-[11px] text-accent-deep hover:underline"
                title="Eine Notiz zu dieser Entscheidung festhalten"
              >
                {notizFuer === a.id ? 'ohne Notiz' : '+ Notiz'}
              </button>
              {/**
               * **Zurück in Arbeit gehört daneben.** Eine Abnahme, die nur „ja" kennt, ist
               * keine – und wer ablehnen will, soll dafür nicht das Detail öffnen müssen.
               */}
              <Button
                size="sm"
                variant="ghost"
                disabled={setzen.isPending}
                onClick={() => entscheiden(a.id, 'in_arbeit')}
              >
                zurück
              </Button>

              {notizFuer === a.id && (
                <textarea
                  autoFocus
                  value={notiz}
                  onChange={(e) => setNotiz(e.target.value)}
                  rows={2}
                  placeholder="Was war der Grund? Steht später in der Rückschau."
                  className="mt-1 w-full resize-y rounded-lg border border-line2 px-2 py-1.5 text-[12.5px] outline-none focus:border-accent"
                />
              )}
            </div>
          ))}
          <p className="mt-2 text-[11px] text-muted">
            Abnehmen setzt die Aufgabe auf <strong>Erledigt</strong> – bei importierten Aufgaben
            schreibt Work das ins Quellsystem zurück. Die Notiz ist freiwillig und steht danach in
            der Rückschau.
          </p>
        </CardPad>
      </Card>
    </>
  )
}
